import { FunctionComponent, useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { Country, PaymentType } from '@afterpay/types'
import { useFlag } from '@afterpaytouch/feature'
import { Cohort } from '@afterpaytouch/portal-api/types'
import { Route } from '../../config/router'
import { CheckoutStatus } from '../../model/checkout'
import { isCountryNeedsBiometricVerification, isCountryUsePersonaFlow } from '../../model/country'
import { Features } from '../../model/features'
import { useCheckoutError, useCheckoutState, useCheckoutStatus, useCohort, useCountry, useIsInlinePaymentOptions, useSelectedPaymentType } from '../../state'
import { isCashGrantHandoffPending } from './cashAuthJourney'
import { IDENTITY_VERIFICATION_SIGNALS, USER_JOURNEY_NAMES, USER_JOURNEY_TIMEOUT_LATE_FIRE_GRACE_MS, USER_JOURNEY_TIMEOUT_MS } from './constants'
import { useJourneyNetworkSignals } from './networkSignals'
import { onBeforeRumSessionStop } from './sessionStopSignal'
import { mapTerminalErrorToJourney } from './terminalErrorMapping'
import { JourneyOutcome, JourneyTerminationReason } from './types'
import { useUserJourneyContext } from './UserJourneyContext'

/** Region-agnostic variant = flow cohort; region rollout is LD country targeting, region itself is a tag */
const COHORT_VARIANTS: Record<Cohort, string> = {
  [Cohort.CashOnly]: 'cash-only',
  [Cohort.APOnly]: 'ap-only',
  [Cohort.NetNew]: 'net-new',
}

/**
 * START anchor — call from the checkout entry page (IndexEntry). Starts the
 * afterpay-checkout journey once per page session, gated by the instrumentation flag.
 * The payment type is unknowable this early (selectedPaymentType only resolves at the
 * payment-selection UI), so every checkout starts a journey; Pay Monthly checkouts exit
 * via the PCL termination in CheckoutJourneyAnchors once the customer commits into the
 * consumer-lending flow. No
 * country conditional by design: region rollout is a LaunchDarkly targeting decision,
 * not code.
 */
export const useStartCheckoutJourney = (): void => {
  const enabled = useFlag(Features.USER_JOURNEY_INSTRUMENTATION)
  const { startJourney } = useUserJourneyContext()
  const started = useRef(false)

  useEffect(() => {
    if (!enabled || started.current) {
      return
    }
    started.current = true
    startJourney(USER_JOURNEY_NAMES.CHECKOUT)
  }, [enabled, startJourney])
}

/**
 * FAILED/terminal anchor — call from the /oh-no page. Completes the journey with the
 * outcome and friction/frustration signal from the reviewed terminal-error mapping
 * table; expected business declines end as USER_CANCELED + friction so only real system
 * failures dip the SLI. Recoverable errors (/oh-no/[error]) never reach here — they keep
 * the journey open and dead-end back to /oh-no if unrecoverable. Fires on mount, so it
 * records the terminal outcome before the page's back-to-store button can reach
 * useCloseCheckout's USER_CANCELED; whichever completes first wins, the other no-ops.
 */
export const useCompleteCheckoutJourneyOnTerminalError = (): void => {
  const { active, addFriction, addFrustration, completeJourney } = useUserJourneyContext()
  const checkoutError = useCheckoutError()

  useEffect(() => {
    if (active?.name !== USER_JOURNEY_NAMES.CHECKOUT) {
      return
    }
    const { outcome, signalType, signal } = mapTerminalErrorToJourney(checkoutError?.error, checkoutError?.errorCode)
    if (signalType === 'friction') {
      addFriction(USER_JOURNEY_NAMES.CHECKOUT, signal)
    } else {
      addFrustration(USER_JOURNEY_NAMES.CHECKOUT, signal)
    }
    completeJourney(USER_JOURNEY_NAMES.CHECKOUT, outcome)
  }, [active, checkoutError, addFriction, addFrustration, completeJourney])
}

/**
 * IDV modality by route (CASHDAP-3422) — one tag per journey (first verify surface wins),
 * so per-region dashboards can split identity friction by document (AU/NZ forms),
 * biometric (GB Onfido), persona (CA) and profile-retry (soft ID / address re-entry)
 * without enumerating every page in the flow. Substring matching covers the
 * apple-paylater route mirrors and the dynamic [document] patterns for free. The start
 * hub and generic biometric pages serve both biometric countries, so they classify by
 * country flow (CA = persona): CA enters at the start hub and routes straight into the
 * Persona SDK, and the first-value-wins tag would otherwise lock CA in as 'biometric'.
 */
const idvTypeFromPathname = (pathname: string, country: Country | null | undefined): string | undefined => {
  if (pathname.includes(Route.VERIFY_RETRY)) {
    return 'profile-retry'
  }
  if (!pathname.includes(Route.VERIFY_IDENTITY)) {
    return undefined
  }
  // Address re-entry (PARTIAL_VERIFIED) is a profile-detail retry, not a document check
  if (pathname.includes(Route.VERIFY_ADDRESS)) {
    return 'profile-retry'
  }
  if (pathname.includes(Route.VERIFY_BIOMETRIC_PERSONA)) {
    return 'persona'
  }
  if (pathname.includes('/biometric') || pathname.includes(Route.VERIFY_IDENTITY_START)) {
    // Defer until the country resolves (null pre-setCountry) — the tag effect re-runs
    // on country, and a premature route-only classification would stick (tagged once)
    if (country == null) {
      return undefined
    }
    return isCountryUsePersonaFlow(country) ? 'persona' : 'biometric'
  }
  // The generic /verify-identity landing and the document sub-routes also classify by
  // country flow: CA skips document selection — VerifyIdentityPage immediately replaces
  // the route with the start hub and continues into Persona — and GB's document
  // selection feeds the Onfido biometric capture. Defer until the country resolves,
  // same as the start-hub branch above.
  if (country == null) {
    return undefined
  }
  if (isCountryUsePersonaFlow(country)) {
    return 'persona'
  }
  return isCountryNeedsBiometricVerification(country) ? 'biometric' : 'document'
}

/**
 * Persistent journey anchors — mount once inside UserJourneyProvider in _app (above the
 * per-page layout so it survives route changes):
 * - variant from the convergence cohort once it resolves (undefined until
 *   setConvergenceReadiness / account lookup).
 * - tags (merchantId, country, windowType, idvType) as their values become known;
 *   idvType only if the journey detours through an identity-verification route.
 *   paymentType is deliberately NOT tagged from the live selection: the radio group
 *   mount-preselects the server's top-ranked offer before any user action and the
 *   selection stays reversible until commit, so the tag only lands at a completion
 *   anchor where the real payment type is known. Abandoned journeys carry no
 *   paymentType tag — their selection genuinely never committed.
 * - identity-verification-required friction when startCheckout rejects with an
 *   IDV-eligible code (CheckoutStatus.IdentityIneligible, CASHDAP-3422). Not a terminal:
 *   the same journey continues through the verify flow and completes at its usual
 *   anchors, so a successful IDV still ends SUCCEEDED — with the detour visible.
 * - TERMINATED when the customer commits into the Pay Monthly (PCL) flow: out of scope
 *   for the afterpay-checkout journey this cycle, and TERMINATED without frustration
 *   counts UNKNOWN in the SLI rather than polluting GOOD/BAD. Commitment is the
 *   Continue-click navigation into /payment-options (classic variant); the inline
 *   variant's entry route IS /payment-options with the selection still reversible
 *   there, so it terminates via the PCL branch of the Confirmed anchor instead.
 *   Accepted residuals this cycle: a classic-variant customer who commits and then
 *   switches back via the Pay-in-4 CTA stays TERMINATED (their PBI outcome is not
 *   re-measured), and an inline-variant customer abandoning mid-PCL records
 *   PROCESS_KILLED.
 * - SUCCEEDED when checkout status transitions to Confirmed (the reducer only sets
 *   Confirmed on a successful confirm; declines go to Error → /oh-no). This fires on the
 *   redux transition, safely before close() purges state and navigates away.
 * - network friction/frustration signals from failed critical-path portal-api calls,
 *   bridged in from the axios interceptor (see networkSignals.ts).
 * - PROCESS_KILLED on pagehide — the abandonment anchor (CASHDAP-3407). pagehide fires
 *   on navigation/unload but NOT on tab switches (unlike visibilitychange). Suppressed
 *   during an intentional Cash grant handoff (CASHDAP-3409), which resumes on return
 *   instead. Also registered on the pre-stop signal (sessionStopSignal.ts):
 *   AppContainer's beforeunload handler stops the RUM session, beforeunload precedes
 *   pagehide on desktop unloads, and the SDK silently drops actions stamped after the
 *   session closes — a pagehide-only completion would be deterministically lost there
 *   (iOS Safari never fires beforeunload and is unaffected). Whichever path runs first
 *   wins; the reducer no-ops the second. The journey dispatch is synchronous, but
 *   delivery is still best-effort: the RUM SDK's own unload flush listeners registered
 *   first and may beacon the batch before this action lands in it. The analytical
 *   started−terminal derivation is the primary abandonment measure; week-6 validation
 *   should compare PROCESS_KILLED volume against it to measure delivery. Journeys
 *   already completed (SUCCEEDED fires before close() navigates) no-op. Intentional
 *   handoffs complete TERMINATED before navigating — cross-region
 *   (useXbtBrowserRedirect in utils/redirects.ts) and the LCCC Pay Monthly credit
 *   application (useCreditFacilityRedirect) — so their unload never reaches this
 *   anchor. A bfcache
 *   restore after this fires loses the journey — accepted, checkout is one-shot.
 * - TERMINATED/TIMEOUT_* when the journey outlives the canonical 10-minute framework
 *   timeout: TIMEOUT_WITH_IDLE_USER when the tab is hidden at the deadline or the
 *   callback fires late (hidden tabs throttle/freeze timers, so a late fire means the
 *   user was idle at the deadline even if the tab is visible again by then);
 *   TIMEOUT_WITH_ACTIVE_USER when it fires on time with the tab visible. A late
 *   completion after the timeout is not recorded (reducer no-op), matching the mobile
 *   framework.
 * Effect order matters: variant/tags run before the completion effects, so a completion
 * landing on the same render still carries them (dispatch is synchronous via stateRef).
 */
export const CheckoutJourneyAnchors: FunctionComponent = () => {
  const { active, addFriction, addTag, completeJourney, setVariant } = useUserJourneyContext()
  useJourneyNetworkSignals()
  const status = useCheckoutStatus()
  const cohort = useCohort()
  const country = useCountry()
  const paymentType = useSelectedPaymentType()
  const isInlinePaymentOptions = useIsInlinePaymentOptions()
  const { initial, checkout } = useCheckoutState()
  const { pathname } = useRouter()

  const journeyActive = active?.name === USER_JOURNEY_NAMES.CHECKOUT

  useEffect(() => {
    if (journeyActive && active?.variant === undefined && cohort !== undefined) {
      setVariant(USER_JOURNEY_NAMES.CHECKOUT, COHORT_VARIANTS[cohort])
    }
  }, [journeyActive, active, cohort, setVariant])

  useEffect(() => {
    if (!journeyActive || active === undefined) {
      return
    }
    const tagOnce = (key: string, value: string | number | null | undefined): void => {
      // == null covers null too: countrySelector yields null (not undefined) until
      // initial setup dispatches setCountry, and a premature tag sticks (tagged once).
      // Deliberately not !value — 0 and false are real values a tag may carry.
      if (value == null || value === '' || active.tags.some((tag) => tag.startsWith(`${key}:`))) {
        return
      }
      addTag(USER_JOURNEY_NAMES.CHECKOUT, `${key}:${value}`)
    }
    tagOnce('merchantId', checkout?.merchant.id)
    tagOnce('country', country)
    // checkout.initial is a populated cold-start default (token '') until
    // performInitialSetup dispatches setInitialData — deriving windowType from the
    // default would permanently tag 'redirect' on iframe/popup sessions, which have
    // the longest init window (the zoid cross-window handshake).
    tagOnce('windowType', !initial?.token ? undefined : initial.isIframe ? 'iframe' : initial.isWindowed ? 'popup' : 'redirect')
    tagOnce('idvType', idvTypeFromPathname(pathname, country))
  }, [journeyActive, active, addTag, checkout, country, initial, pathname])

  useEffect(() => {
    if (!journeyActive || active === undefined || status !== CheckoutStatus.IdentityIneligible) {
      return
    }
    // Only count the detour once the customer actually reaches a verification page.
    // GB/CA can reject /start while identities are already VERIFIED and silently
    // retry without routing anywhere (useStartCheckout's shouldRetry path) — that
    // transient IdentityIneligible is invisible to the customer and must not
    // inflate regional IDV friction.
    if (idvTypeFromPathname(pathname, country) === undefined) {
      return
    }
    if (!active.frictions.includes(IDENTITY_VERIFICATION_SIGNALS.REQUIRED)) {
      addFriction(USER_JOURNEY_NAMES.CHECKOUT, IDENTITY_VERIFICATION_SIGNALS.REQUIRED)
    }
  }, [journeyActive, active, status, addFriction, pathname, country])

  useEffect(() => {
    if (!journeyActive || active === undefined || paymentType !== PaymentType.PCL) {
      return
    }
    // Commitment, not selection: PaymentTypesRadioGroup mount-preselects the server's
    // top-ranked offer (which can be PCL) before any user action, and the selection can
    // flip back to PBI (radio click, setPaymentOptionsError's forced revert, the
    // Pay-in-4 CTA) — completing on the raw selection would end the only journey of a
    // checkout that ultimately pays with Pay in 4, because completed journeys are
    // deleted and useStartCheckoutJourney starts once per page session. The classic
    // variant commits via the Continue-click navigation into the consumer-lending
    // options flow; the inline variant's entry route IS /payment-options with the
    // selection still reversible there, so it is excluded here and terminates via the
    // PCL branch of the Confirmed anchor below.
    if (isInlinePaymentOptions || !pathname.includes(Route.PAYMENT_OPTIONS)) {
      return
    }
    if (!active.tags.some((tag) => tag.startsWith('paymentType:'))) {
      addTag(USER_JOURNEY_NAMES.CHECKOUT, `paymentType:${PaymentType.PCL}`)
    }
    completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.TERMINATED)
  }, [journeyActive, active, paymentType, pathname, isInlinePaymentOptions, addTag, completeJourney])

  useEffect(() => {
    if (!journeyActive || active === undefined || status !== CheckoutStatus.Confirmed) {
      return
    }
    // paymentType tags at completion because the live selection is reversible until
    // commit (see the PCL anchor above). The confirm path treats an unresolved selector
    // as the default Pay-in-4 flow (see useConfirmCheckoutArgs), so a journey confirmed
    // without a resolved selection is PBI — without the default, payment-type
    // segmentation misses the majority path. The once-guard protects journeys that
    // already carry the tag (e.g. resumed with annotations).
    if (!active.tags.some((tag) => tag.startsWith('paymentType:'))) {
      addTag(USER_JOURNEY_NAMES.CHECKOUT, `paymentType:${paymentType ?? PaymentType.PBI}`)
    }
    if (paymentType === PaymentType.PCL) {
      // Safety net for the inline payment-options variant: a Pay Monthly confirmation
      // is out of the afterpay-checkout journey's scope this cycle — TERMINATED, not
      // SUCCEEDED, so PCL successes classify the same as classic-variant commitments
      // and never inflate the Pay-in-4 SLI.
      completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.TERMINATED)
      return
    }
    completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.SUCCEEDED)
  }, [journeyActive, active, status, paymentType, addTag, completeJourney])

  useEffect(() => {
    if (!journeyActive) {
      return undefined
    }
    const recordProcessKilled = (): void => {
      // An intentional Cash grant handoff is not an abandonment — the marker survives
      // (annotated) and the return page load resumes the journey (cashAuthJourney.ts)
      if (isCashGrantHandoffPending()) {
        return
      }
      completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.PROCESS_KILLED)
    }
    // Pre-stop signal first: on desktop unloads AppContainer's beforeunload handler
    // stops the RUM session before pagehide fires, which would drop this completion
    const unsubscribe = onBeforeRumSessionStop(recordProcessKilled)
    window.addEventListener('pagehide', recordProcessKilled)
    return () => {
      unsubscribe()
      window.removeEventListener('pagehide', recordProcessKilled)
    }
  }, [journeyActive, completeJourney])

  // Primitive deps: the active object gets a new identity on every tag/signal, but the
  // timer only needs to reset per journey instance
  const activeJourneyStarted = active?.created
  useEffect(() => {
    if (!journeyActive || activeJourneyStarted === undefined) {
      return undefined
    }
    // Anchored to the journey's start time, so a reset keeps the same absolute deadline
    // rather than extending it
    const deadline = activeJourneyStarted + USER_JOURNEY_TIMEOUT_MS
    const remaining = Math.max(0, deadline - Date.now())
    const timer = window.setTimeout(() => {
      // Hidden tabs (mobile especially) throttle or freeze timers, so this callback can
      // first run after re-foregrounding, when visibilityState is back to 'visible'.
      // Firing well past the anchored deadline means the user was idle at the deadline,
      // whatever visibility reads now.
      const firedLate = Date.now() - deadline > USER_JOURNEY_TIMEOUT_LATE_FIRE_GRACE_MS
      const reason =
        firedLate || document.visibilityState === 'hidden' ? JourneyTerminationReason.TIMEOUT_WITH_IDLE_USER : JourneyTerminationReason.TIMEOUT_WITH_ACTIVE_USER
      completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.TERMINATED, reason)
    }, remaining)
    return () => {
      window.clearTimeout(timer)
    }
  }, [journeyActive, activeJourneyStarted, completeJourney])

  return null
}
